/* eslint-disable react-hooks/exhaustive-deps */
import React, {FC, createContext, useContext, useEffect, useState} from 'react'
import {WithChildren} from '../../helpers'
import {Link, useNavigate} from 'react-router-dom'
import {useAuth} from '../../../app/modules/auth'
import { collection, doc, onSnapshot, query, serverTimestamp, updateDoc, where} from 'firebase/firestore'
import db from '../../../services/firebase'
import Button from 'app/components/Button'

export interface PageLink {
  title: string
  path: string
  isActive: boolean
  isSeparator?: boolean
}

export interface PageDataContextModel {
  pageTitle?: string
  setPageTitle: (_title: string) => void
  pageDescription?: string
  setPageDescription: (_description: string) => void
  pageBreadcrumbs?: Array<PageLink>
  setPageBreadcrumbs: (_breadcrumbs: Array<PageLink>) => void
}

const PageDataContext = createContext<PageDataContextModel>({
  setPageTitle: (_title: string) => {},
  setPageBreadcrumbs: (_breadcrumbs: Array<PageLink>) => {},
  setPageDescription: (_description: string) => {},
})

const PageDataProvider: FC<WithChildren> = ({children}) => {
  const [pageTitle, setPageTitle] = useState<string>('')
  const [pageDescription, setPageDescription] = useState<string>('')
  const [pageBreadcrumbs, setPageBreadcrumbs] = useState<Array<PageLink>>([])
  const value: PageDataContextModel = {
    pageTitle,
    setPageTitle,
    pageDescription,
    setPageDescription,
    pageBreadcrumbs,
    setPageBreadcrumbs,
  }
  return <PageDataContext.Provider value={value}>{children}</PageDataContext.Provider>
}

function usePageData() {
  return useContext(PageDataContext)
}

type Props = {
  description?: string
  breadcrumbs?: Array<PageLink>
  title?: string
  icon?: string
  desc?: string
}

const DashPageTitle: FC<Props & WithChildren> = ({
  children,
  description,
  breadcrumbs,
  title,
  icon,
  desc,
}) => {
  const {setPageTitle, setPageDescription, setPageBreadcrumbs} = usePageData()
  const [grandTotal, setGrandTotal] = useState(0)
  const [policyTotal, setPolicyTotal] = useState(0)
  const [total, setTotal] = useState(0)
  const navigate = useNavigate()
  const [personnelPoliciesLength, setPersonnelPoliciesLength] = useState(0)
  const {currentUser} = useAuth()
  const [showEmptyState, setShowEmptyState] = useState<boolean>(false)
  const [employeesData, setemployeesData] = useState<any[]>([]);
  const [activeFrameworks, setActiveFrameworks] = useState<any[]>([])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setShowEmptyState(true)
    }, 1000)

    return () => clearTimeout(timeoutId)
  }, [])

  useEffect(() => {
    fetchData()
    const unsubscribePersonnel = getPersonnel()
    
    return () => {
      if (unsubscribePersonnel) {
        unsubscribePersonnel()
      }
    }
  }, [])

  useEffect(() => {
    let unsubscribeFrameworks: (() => void) | undefined;

    const setupFrameworkSubscription = async () => {
      try {
        const tenantId = currentUser?.tenantId;
        if (!tenantId) {
          setActiveFrameworks([]);
          return;
        }

        const tenantRef = doc(db, "tenants", tenantId);
        const frameworksCollectionRef = collection(tenantRef, "active_frameworks");

        unsubscribeFrameworks = onSnapshot(
          frameworksCollectionRef,
          (snapshot) => {
            const frameworksData = snapshot.docs.map((doc) => ({
              ...doc.data(),
              id: doc.id,
              frameworks: doc.data().frameworks,
            }));

            const frameworks = frameworksData[0]?.frameworks || [];
            setActiveFrameworks(frameworks);
          },
          (error) => {
            console.error("Firestore subscription error:", error);
          }
        );
      } catch (error) {
        console.error("Setup subscription error:", error);
      }
    };

    setupFrameworkSubscription();

    return () => {
      if (unsubscribeFrameworks) {
        unsubscribeFrameworks();
      }
    };
  }, [currentUser?.tenantId]);

  const fetchData = async () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      if (!tenantId) {
        return
      }

      const tenantRef = doc(db, 'tenants', tenantId)
      const PolicyRef = collection(tenantRef, 'employees')
      const unsubscribe = onSnapshot(PolicyRef, (snapshot) => {
        const employeesData = snapshot.docs
          .map((doc) => ({
            ...doc.data(),
            id: doc.id,
            fullName: doc.data().full_name,
            group: doc.data().group,
            uid: doc.data().uid,
          }))
          .filter((data) => data.uid === currentUser?.uid)

        if (employeesData.length > 0) {
          const group = employeesData[0].group
          const groupRef = collection(tenantRef, 'personnel_group')
          const unsubscribeGroup = onSnapshot(groupRef, (snapshot) => {
            const groupData = snapshot.docs
              .map((doc) => ({
                ...doc.data(),
                id: doc.id,
                checklist: doc.data().checklist,
                groupName: doc.data().groupName,
                uid: doc.data().uid,
              }))
              .filter((data) => data.groupName === group)

            if (groupData.length > 0) {
              const checklist = groupData[0].checklist
              const checklistRef = collection(tenantRef, 'personnel_checklist')
              const unsubscribeChecklist = onSnapshot(checklistRef, (snapshot) => {
                const checklistData = snapshot.docs
                  .map((doc) => ({
                    ...doc.data(),
                    id: doc.id,
                    checklist: doc.data().checklist_name,
                    policies: doc.data().policies,
                  }))
                  .filter((data) => data.checklist === checklist)
                if (checklistData.length > 0) {
                  setPersonnelPoliciesLength(
                    checklistData[0].policies ? checklistData[0].policies.length : ''
                  )
                } else {
                  setPersonnelPoliciesLength(0)
                }
              })
            }
          })
        }
      })

      return () => {
        unsubscribe()
      }
    } catch (error) {
      console.error('Error fetching data:', error)
    }
  }

  useEffect(() => {
    setTotal(grandTotal + policyTotal)
    const derivedTotal = grandTotal + policyTotal

    if (derivedTotal === 100) {
      return
    }

    setTotal(derivedTotal)
  }, [grandTotal, policyTotal, total, navigate])

  useEffect(() => {
    const fetchPoliciesData = async () => {
      try {
        const tenantId = currentUser?.tenantId || ''
        const userId = currentUser?.uid || ''

        if (!tenantId || !userId) {
          return
        }

        const policiesRef = collection(db, 'tenants', tenantId, 'onboarding', userId, 'policies')
        const unsubscribe = onSnapshot(policiesRef, (snapshot) => {
          const policyDataMap = {}
          let checkedCount = 0

          if (snapshot.empty) {
            setPolicyTotal(0)
            return
          }

          snapshot.docs.forEach((doc) => {
            const policyData = doc.data() || {}
            const isChecked = policyData.isChecked || false

            policyDataMap[doc.id] = policyData

            if (isChecked) {
              checkedCount += 1
            }
          })

          const allChecked = checkedCount === personnelPoliciesLength
          setPolicyTotal(allChecked ? 50 : 0)
        })

        return () => {
          unsubscribe()
        }
      } catch (error) {
        console.error('Error fetching policies data:', error)
      }
    }

    fetchPoliciesData()
  }, [currentUser, personnelPoliciesLength])

  useEffect(() => {
    let unsubscribe = () => {}

    const fetchTrainingData = async () => {
      try {
        const tenantId = currentUser?.tenantId || ''
        const userId = currentUser?.uid || ''

        if (!tenantId || !userId) {
          return
        }

        let isSATChecked = false
        let isGDPRChecked = false

        const updateTotalCompleted = () => {
          if (activeFrameworks.includes('GDPR')) {
            setGrandTotal(isSATChecked && isGDPRChecked ? 50 : 0)
          } else {
            setGrandTotal(isSATChecked ? 50 : 0)
          }
        }
        const SATDocRef = doc(db, 'tenants', tenantId, 'onboarding', userId, 'training', 'SAT')
        const GDPRDocRef = doc(db, 'tenants', tenantId, 'onboarding', userId, 'training', 'GDPR')
        const unsubscribeSAT = onSnapshot(SATDocRef, (doc) => {
          const SATPCISecureData = doc.data() || {}
          isSATChecked = SATPCISecureData.is_checked || false
          updateTotalCompleted()
        })

        const unsubscribeGDPR = onSnapshot(GDPRDocRef, (doc) => {
          const GDPRData = doc.data() || {}
          isGDPRChecked = GDPRData.is_checked || false
          updateTotalCompleted()
        })

        unsubscribe = () => {
          unsubscribeSAT()
          unsubscribeGDPR()
        }
      } catch (error) {
        console.error('Error fetching training data:', error)
      }
    }

    fetchTrainingData()

    return () => {
      unsubscribe()
    }
  }, [currentUser,activeFrameworks])

  const getPersonnel = () => {
    try {
      const tenantId = currentUser?.tenantId || '';
      const uid = currentUser?.uid || ''; 
      const tenantRef = doc(db, 'tenants', tenantId);
      const policyRef = collection(tenantRef, 'employees');
  
      let personnelQuery = query(policyRef);
  
      if (tenantId && uid) {
        personnelQuery = query(
          policyRef,
          where('tenantId', '==', tenantId),
          where('uid', '==', uid)
        );
      }
  
      const unsubscribe = onSnapshot(personnelQuery, (snapshot) => {
        const risksData = snapshot.docs
          .map((doc) => ({
            ...doc.data(),
            id: doc.id,
            role: doc.data().role,
          }))
          .filter((item) => item.role !== 'Auditor');
  
        setemployeesData(risksData);
      });
  
      return unsubscribe;
    } catch (error) {
      console.error('Error getting personnel:', error);
    }
  };

  useEffect(() => {
    if (children) {
      setPageTitle(children.toString())
    }
    return () => {
      setPageTitle('')
    }
  }, [children])

  useEffect(() => {
    if (description) {
      setPageDescription(description)
    }
    return () => {
      setPageDescription('')
    }
  }, [description])

  useEffect(() => {
    if (breadcrumbs) {
      setPageBreadcrumbs(breadcrumbs)
    }
    return () => {
      setPageBreadcrumbs([])
    }
  }, [breadcrumbs])

  const handleClose = async (e: React.FormEvent)  =>{
      e.preventDefault()
  
      try {
        const payload = {
          show_onboarding_state: false,
        }
  
        if (currentUser) {
          const tenantId = currentUser?.tenantId || ''
          const tenantRef = doc(db, 'tenants', tenantId)

        const assetRef = doc(tenantRef, 'employees', employeesData[0]?.id || '')
        await updateDoc(assetRef, {
          ...payload,
          updated_at: serverTimestamp(),
        })
      }

      } catch (error) {
        console.error('Error adding policy:', error)
      } 
  }

  return (
    <>
      {employeesData[0]?.show_onboarding_state !== false &&  showEmptyState && total !== 100 && (
        <div
          className='card'
          style={{backgroundColor: '#F3FAF7', border: '1.5px solid #0E9F6E',marginBottom:'24px'}}
        >
          <div className='px-6 py-4'>
            <div className='d-flex align-items-between justify-content-between'>
              <div className='d-flex justify-content-start flex-column gap-1'>
                <span className='card-label fw-semibold fs-4'>{title}</span>
                <span className=' fw-normal fs-6' style={{color: '#5A5A5A'}}>
                  You have{' '}
                  {grandTotal === 50 && policyTotal === 50
                    ? '2 of 2'
                    : (grandTotal === 50 ? 1 : 0) + (policyTotal === 50 ? 1 : 0) === 2
                    ? '2 of 2'
                    : grandTotal === 50 || policyTotal === 50
                    ? '1 of 2'
                    : '0 of 2'}{' '}
                  onboarding tasks remaining. Let’s get those completed to ensure compliance.
                </span>
              </div>

              <div className='d-flex align-items-center gap-2'>
                <Link to='/training'>
                  <Button
                    btnType='primary'
                  >
                    Continue
                  </Button>
                </Link>
                <button className='btn btn-sm btn-active-light-primary px-3' onClick={handleClose}>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='18'
                    height='18'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    stroke-width='2'
                    stroke-linecap='round'
                    stroke-linejoin='round'
                    className='icon icon-tabler icons-tabler-outline icon-tabler-x'
                  >
                    <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                    <path d='M18 6l-12 12' />
                    <path d='M6 6l12 12' />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

const PageDescription: FC<WithChildren> = ({children}) => {
  const {setPageDescription} = usePageData()
  useEffect(() => {
    if (children) {
      setPageDescription(children.toString())
    }
    return () => {
      setPageDescription('')
    }
  }, [children])
  return <></>
}

export {PageDescription, DashPageTitle, PageDataProvider, usePageData}
